import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import CloseIcon from "@mui/icons-material/Close";
import PlayCircleIcon from "@mui/icons-material/PlayCircle";
import useSignedUrl from "../../utils/useSignedUrl";
import { getMediaFallbackUrl } from "../../utils/mediaFallbacks";
import { reportAppError } from "../../utils/observability";
import { normalizeStoryRouteTarget } from "./ctaTargets";
import ContentReviewChip from "../../components/ContentReviewChip";
import { COLORS } from "../../theme/tokens";

const HERO_MEDIA = {
  videoWebm: "media/videos/Promo-De-Windroos_V3-web-720p.webm",
  videoMp4: "media/videos/Promo-De-Windroos_V3-web-720p.mp4",
  desktopPoster: "media/videos/Promo-De-Windroos_V3-poster-desktop.jpg",
  mobilePoster: "media/videos/Promo-De-Windroos_V3-poster-mobile.jpg",
  fallbackImage: "media/images/Werkhoeken.jpg",
};

// "Welkom" replaces the earlier "Welkomstfilm" label because the anchor
// scrolls back to the hero rather than triggering the separate film dialog.
// This strip orients visitors within the story. Conversion remains owned by
// the permanent header action and the final conversion section.
const ROUTE_STRIP = [
  { label: "Welkom", href: "#welcome" },
  { label: "In de klas", href: "#in-de-klas" },
  { label: "Freinet", href: "#freinet" },
  { label: "Onze school", href: "#school" },
  { label: "Nieuws", href: "#nieuws" },
];

function CtaButton({ cta, variant }) {
  if (!cta?.label) return null;
  const target = normalizeStoryRouteTarget(cta.anchor || cta.route || "#");
  const isPrimary = variant === "contained";
  return (
    <Button
      variant={variant}
      size="large"
      href={target}
      sx={{
        minHeight: 48,
        px: 3,
        fontWeight: 800,
        borderRadius: 1.5,
        textTransform: "none",
        boxShadow: isPrimary ? "0 10px 24px rgba(239, 125, 0, 0.32)" : "none",
        backgroundColor: isPrimary ? COLORS.orange : "rgba(255, 255, 255, 0.08)",
        color: isPrimary ? COLORS.ink : "#fff",
        borderColor: isPrimary ? "transparent" : "rgba(255, 255, 255, 0.72)",
        "&:hover": {
          backgroundColor: isPrimary ? "#E87800" : "rgba(255, 255, 255, 0.18)",
          borderColor: isPrimary ? "transparent" : "#fff",
        },
      }}
    >
      {cta.label}
    </Button>
  );
}

function HeroBackground({ block }) {
  const desktopPosterKey = block?.content?.desktopPosterS3Key || HERO_MEDIA.desktopPoster;
  const mobilePosterKey = block?.content?.mobilePosterS3Key || HERO_MEDIA.mobilePoster;
  const useMobilePoster = useMediaQuery("(max-width: 599px)");
  const posterKey = useMobilePoster ? mobilePosterKey : desktopPosterKey;
  const poster = useSignedUrl(posterKey);
  const legacyImageUrl = getMediaFallbackUrl(HERO_MEDIA.fallbackImage);
  const [posterError, setPosterError] = useState(false);
  const fallbackNeeded = posterError || poster.status === "error";
  const { url: fallbackImageUrl } = useSignedUrl(
    fallbackNeeded ? HERO_MEDIA.fallbackImage : ""
  );

  useEffect(() => {
    setPosterError(false);
  }, [posterKey]);

  const posterUrl = fallbackNeeded
    ? fallbackImageUrl || legacyImageUrl
    : poster.url;

  return (
    <>
      {posterUrl ? (
        <Box
          component="img"
          src={posterUrl}
          alt=""
          fetchPriority="high"
          onError={() => setPosterError(true)}
          aria-hidden="true"
          sx={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            zIndex: 0,
            objectFit: "cover",
          }}
        />
      ) : (
        <Box
          aria-hidden="true"
          sx={{
            position: "absolute",
            inset: 0,
            background: `linear-gradient(135deg, ${COLORS.blue} 0%, #1f3a3d 100%)`,
            zIndex: 0,
          }}
        />
      )}
      <Box
        aria-hidden="true"
        sx={{
          position: "absolute",
          inset: 0,
          background:
            "linear-gradient(90deg, rgba(32,48,51,0.72), rgba(32,48,51,0.18)), linear-gradient(0deg, rgba(32,48,51,0.52), rgba(32,48,51,0))",
          zIndex: 1,
        }}
      />
    </>
  );
}

function WelcomeVideoDialog({ block, open, onClose }) {
  const videoWebmKey = block?.content?.optimizedWebmS3Key || HERO_MEDIA.videoWebm;
  const videoMp4Key = block?.content?.optimizedS3Key || HERO_MEDIA.videoMp4;
  const posterKey = block?.content?.desktopPosterS3Key || HERO_MEDIA.desktopPoster;
  const webm = useSignedUrl(open ? videoWebmKey : "");
  const mp4 = useSignedUrl(open ? videoMp4Key : "");
  const { url: posterUrl } = useSignedUrl(open ? posterKey : "");
  const [videoError, setVideoError] = useState(false);

  useEffect(() => {
    if (open) setVideoError(false);
  }, [open]);

  function handleVideoError() {
    reportAppError({
      stage: "video_render",
      errorCode: "render",
      mediaCategory: "video",
    });
    setVideoError(true);
  }

  const sourcesSettled = [webm.status, mp4.status].every((status) =>
    status === "ready" || status === "error"
  );
  const hasVideo = webm.url || mp4.url;
  const loading = open && !hasVideo && !sourcesSettled;
  const unavailable = videoError || (sourcesSettled && !hasVideo);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      aria-labelledby="welcome-video-title"
      slotProps={{ paper: { sx: { backgroundColor: COLORS.ink, color: "#fff" } } }}
    >
      <DialogTitle id="welcome-video-title" sx={{ pr: 7, fontFamily: "Poppins, sans-serif" }}>
        Welkom op De Windroos
        <IconButton
          aria-label="Welkomstfilm sluiten"
          onClick={onClose}
          sx={{ position: "absolute", right: 12, top: 10, color: "#fff" }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 0, backgroundColor: "#10191b" }}>
        {loading && !unavailable && (
          <Stack sx={{ minHeight: 320, alignItems: "center", justifyContent: "center", gap: 2 }}>
            <CircularProgress aria-label="Welkomstfilm laden" sx={{ color: COLORS.orange }} />
            <Typography>De welkomstfilm wordt geladen…</Typography>
          </Stack>
        )}
        {hasVideo && !unavailable && (
          <Box
            component="video"
            poster={posterUrl || undefined}
            controls
            autoPlay
            playsInline
            preload="metadata"
            onError={handleVideoError}
            sx={{
              display: "block",
              width: "100%",
              maxHeight: "80vh",
              backgroundColor: "#000",
              "&::cue": {
                color: "#fff",
                backgroundColor: "rgba(0, 0, 0, 0.82)",
                fontSize: "clamp(1rem, 2vw, 1.25rem)",
              },
            }}
          >
            {webm.url && <source src={webm.url} type="video/webm" />}
            {mp4.url && <source src={mp4.url} type="video/mp4" />}
            <track
              kind="captions"
              src="/media/Promo-De-Windroos_V3-nl.vtt"
              srcLang="nl"
              label="Nederlands"
              default
            />
          </Box>
        )}
        {unavailable && (
          <Stack sx={{ minHeight: 260, p: 4, alignItems: "center", justifyContent: "center", gap: 1 }}>
            <Typography variant="h4" component="p" sx={{ color: "#fff", textAlign: "center" }}>
              De welkomstfilm kan nu niet worden afgespeeld
            </Typography>
            <Typography sx={{ color: "rgba(255,255,255,0.82)", textAlign: "center" }}>
              De rest van de website blijft gewoon beschikbaar. Probeer het later opnieuw.
            </Typography>
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function HeroSection({ section }) {
  const { content, blocks = [] } = section;
  const videoBlock = blocks.find((b) => b.blockType === "video-placeholder");
  const [videoOpen, setVideoOpen] = useState(false);

  return (
    <Box
      id="welcome"
      component="section"
      sx={{
        position: "relative",
        overflow: "hidden",
        color: "#fff",
        backgroundColor: "#123",
        minHeight: { xs: "calc(100vh - 174px)", md: "calc(100vh - 154px)" },
        display: "grid",
        alignItems: "end",
        scrollMarginTop: { xs: 64, md: 80 },
      }}
    >
      <HeroBackground block={videoBlock} />

      <Box
        sx={{
          position: "relative",
          zIndex: 2,
          width: "100%",
          maxWidth: 1160,
          mx: "auto",
          px: "clamp(20px, 5vw, 72px)",
          pt: "clamp(38px, 6vw, 72px)",
          pb: "clamp(24px, 3vw, 40px)",
        }}
      >
        {content.eyebrow && (
          <Stack direction="row" spacing={1} sx={{ alignItems: "center", flexWrap: "wrap", mb: "10px" }}>
            <Typography
              sx={{
                color: "#fff",
                fontWeight: 800,
                letterSpacing: "0.07em",
                textTransform: "uppercase",
                fontSize: "0.76rem",
                opacity: 0.92,
                textShadow: "0 2px 12px rgba(0,0,0,0.35)",
              }}
            >
              {content.eyebrow}
            </Typography>
            <ContentReviewChip
              status="aangepast"
              sx={{
                backgroundColor: "rgba(255, 255, 255, 0.88)",
                borderColor: "rgba(255, 255, 255, 0.72)",
              }}
            />
          </Stack>
        )}
        {!content.eyebrow && (
          <ContentReviewChip
            status="aangepast"
            sx={{
              mb: "10px",
              backgroundColor: "rgba(255, 255, 255, 0.88)",
              borderColor: "rgba(255, 255, 255, 0.72)",
            }}
          />
        )}
        {content.headline && (
          <Typography
            variant="h1"
            sx={{
              fontFamily: "windroos, Georgia, serif",
              color: "#fff",
              fontSize: "clamp(44px, 7vw, 88px)",
              lineHeight: 1.04,
              maxWidth: 860,
              textShadow: "0 2px 18px rgba(0,0,0,0.35)",
              m: 0,
            }}
          >
            {content.headline}
          </Typography>
        )}
        {content.lead && (
          <Typography
            sx={{
              color: "#fff",
              opacity: 0.95,
              maxWidth: 680,
              mt: "18px",
              fontSize: "clamp(1.02rem, 1.5vw, 1.22rem)",
              lineHeight: 1.6,
              textShadow: "0 2px 12px rgba(0,0,0,0.32)",
            }}
          >
            {content.lead}
          </Typography>
        )}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1.5}
          sx={{ mt: 3 }}
        >
          <CtaButton cta={content.primaryCta} variant="contained" />
          {videoBlock ? (
            <Button
              variant="outlined"
              size="large"
              startIcon={<PlayCircleIcon />}
              onClick={() => setVideoOpen(true)}
              sx={{
                minHeight: 48,
                px: 3,
                fontWeight: 800,
                borderRadius: 1.5,
                textTransform: "none",
                color: "#fff",
                borderColor: "rgba(255, 255, 255, 0.72)",
                backgroundColor: "rgba(255, 255, 255, 0.08)",
                "&:hover": {
                  borderColor: "#fff",
                  backgroundColor: "rgba(255, 255, 255, 0.18)",
                },
              }}
            >
              Bekijk de welkomstfilm
            </Button>
          ) : (
            <CtaButton cta={content.secondaryCta} variant="outlined" />
          )}
        </Stack>
      </Box>

      <Box
        component="nav"
        aria-label="Verhaalroute"
        sx={{
          position: "relative",
          zIndex: 2,
          width: "calc(100% - 40px)",
          maxWidth: 1160,
          mx: "auto",
          mb: "28px",
          borderRadius: 1,
          overflow: "hidden",
          background: "rgba(255, 255, 255, 0.94)",
          border: "1px solid rgba(255, 255, 255, 0.72)",
          boxShadow: COLORS.shadow,
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "repeat(5, minmax(0, 1fr))" },
        }}
      >
        {ROUTE_STRIP.map((step, idx) => {
          const isLast = idx === ROUTE_STRIP.length - 1;
          return (
            <Box
              key={step.href}
              component="a"
              href={step.href}
              sx={{
                px: "14px",
                py: "12px",
                textAlign: "center",
                fontFamily: "windroos, Georgia, serif",
                fontWeight: 400,
                fontSize: "1rem",
                color: COLORS.ink,
                backgroundColor: "transparent",
                textDecoration: "none",
                borderRight: {
                  xs: "none",
                  sm: isLast ? "none" : `1px solid ${COLORS.line}`,
                },
                borderBottom: {
                  xs: isLast ? "none" : `1px solid ${COLORS.line}`,
                  sm: "none",
                },
                "&:hover": {
                  backgroundColor: "rgba(60, 143, 153, 0.08)",
                },
              }}
            >
              {step.label}
            </Box>
          );
        })}
      </Box>
      <WelcomeVideoDialog
        block={videoBlock}
        open={videoOpen}
        onClose={() => setVideoOpen(false)}
      />
    </Box>
  );
}
